import React, {
  useEffect,
  useMemo,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import { fetchMenu } from "../../actions/menuActions";

import { useCart } from "../../components/CartContext";

import "./css/HomeScreen.css";


function HomeScreen() {

  const dispatch = useDispatch();

  const navigate = useNavigate();


  // ======================================================
  // URL PARAMS
  // ======================================================

  const {
    restaurantId,
    tableId,
  } = useParams();


  // ======================================================
  // CART
  // ======================================================

  const {
    addToCart,
    cartCount,
    cartTotal,
  } = useCart();


  // ======================================================
  // REDUX
  // ======================================================

  const menuState = useSelector(
    (state) => state.menu
  );


  const themeState = useSelector(
    (state) => state.theme
  );


  // ======================================================
  // RESTAURANT NAME
  // ======================================================

  const restaurantName =
    themeState?.restaurantName ||
    menuState?.restaurantName ||
    "Indian Restaurant";


  // ======================================================
  // MENU FROM REDUX
  //
  // Reducer stores:
  //
  // state.menu.categories
  //
  // We are NOT changing the reducer.
  // ======================================================

  const categoriesFromRedux =
    menuState?.categories || [];


  // ======================================================
  // FETCH MENU
  // ======================================================

  useEffect(() => {

    if (restaurantId) {

      dispatch(
        fetchMenu(restaurantId)
      );

    }

  }, [
    dispatch,
    restaurantId,
  ]);


  // ======================================================
  // SORT CATEGORIES AND MENU ITEMS
  // ======================================================

  const categories = useMemo(() => {

    if (
      !Array.isArray(
        categoriesFromRedux
      )
    ) {

      return [];

    }


    return [
      ...categoriesFromRedux,
    ]

      // --------------------------------------------------
      // ACTIVE CATEGORIES ONLY
      // --------------------------------------------------

      .filter(
        (category) =>
          category &&
          category.is_active !== false
      )


      // --------------------------------------------------
      // CATEGORY ORDER
      // --------------------------------------------------

      .sort(
        (a, b) =>
          Number(
            a.display_order || 0
          ) -
          Number(
            b.display_order || 0
          )
      )


      // --------------------------------------------------
      // SORT ITEMS INSIDE EACH CATEGORY
      // --------------------------------------------------

      .map((category) => {

        const items =
          Array.isArray(
            category.items
          )
            ? category.items
            : [];


        return {

          ...category,

          items: [
            ...items,
          ]

            // --------------------------------------------
            // AVAILABLE ITEMS ONLY
            // --------------------------------------------

            .filter(
              (item) =>
                item &&
                item.is_available !== false
            )


            // --------------------------------------------
            // ITEM ORDER
            // --------------------------------------------

            .sort(
              (a, b) =>
                Number(
                  a.display_order || 0
                ) -
                Number(
                  b.display_order || 0
                )
            ),

        };

      });

  }, [
    categoriesFromRedux,
  ]);


  // ======================================================
  // FAVOURITES
  //
  // From every category:
  //
  // display_order = 1
  // display_order = 2
  //
  // 6 categories x 2 items = 12 favourites
  // ======================================================

  const favouriteItems = useMemo(() => {

    const favourites = [];


    categories.forEach(
      (category) => {

        const firstTwo =
          category.items.filter(
            (item) => {

              const order =
                Number(
                  item.display_order
                );


              return (
                order === 1 ||
                order === 2
              );

            }
          );


        firstTwo.forEach(
          (item) => {

            favourites.push({

              ...item,

              categoryName:
                category.name,

            });

          }
        );

      }
    );


    return favourites;

  }, [
    categories,
  ]);


  // ======================================================
  // SCROLL TO MENU
  // ======================================================

  const scrollToMenu = () => {

    const menuElement =
      document.getElementById(
        "menu"
      );


    if (menuElement) {

      menuElement.scrollIntoView({

        behavior: "smooth",

        block: "start",

      });

    }

  };


  // ======================================================
  // SCROLL TO CATEGORY
  // ======================================================

  const scrollToCategory = (
    categoryId
  ) => {

    const categoryElement =
      document.getElementById(
        `t6-category-${categoryId}`
      );


    if (categoryElement) {

      categoryElement.scrollIntoView({

        behavior: "smooth",

        block: "start",

      });

    }

  };


  // ======================================================
  // ADD TO CART
  // ======================================================

  const handleAddToCart = (
    item
  ) => {

    addToCart(item);

  };


  // ======================================================
  // OPEN CART
  // ======================================================

  const openCart = () => {

    navigate(
      `/${restaurantId}/table/${tableId}/cart`
    );

  };


  // ======================================================
  // LOADING
  // ======================================================

  if (menuState?.loading) {

    return (

      <div className="t6-loading">

        <div className="t6-spinner"></div>

        <p>
          Preparing your table...
        </p>

      </div>

    );

  }


  // ======================================================
  // ERROR
  // ======================================================

  if (menuState?.error) {

    return (

      <div className="t6-error">

        <div className="t6-error-box">

          <div className="t6-error-icon">
            !
          </div>


          <h2>
            Unable to load menu
          </h2>


          <p>

            {typeof menuState.error === "string"
              ? menuState.error
              : "Something went wrong while loading the menu."}

          </p>


          <button
            type="button"
            onClick={() =>
              dispatch(
                fetchMenu(
                  restaurantId
                )
              )
            }
          >
            Try Again
          </button>

        </div>

      </div>

    );

  }


  // ======================================================
  // PAGE
  // ======================================================

  return (

    <main className="t6-page">


      {/* ==================================================
          HERO
      ================================================== */}

      <section className="t6-hero">

        <div className="t6-hero-background"></div>

        <div className="t6-hero-overlay"></div>


        <div className="t6-hero-content">

          <p className="t6-eyebrow">
            AUTHENTIC INDIAN CUISINE
          </p>


          <h1>

            Taste the

            <br />

            <span>
              Flavours of India
            </span>

          </h1>


          <p className="t6-hero-description">

            Aromatic spices, regional Indian
            flavours and recipes made with care.

          </p>


          <button
            type="button"
            className="t6-primary-button"
            onClick={scrollToMenu}
          >
            Explore Our Menu
          </button>

        </div>


        <div className="t6-hero-bottom">

          <span>
            SCROLL TO DISCOVER
          </span>

          <div className="t6-scroll-line"></div>

        </div>

      </section>



      {/* ==================================================
          INTRO
      ================================================== */}

      <section className="t6-intro">

        <div className="t6-container t6-intro-inner">

          <p className="t6-label">

            WELCOME TO{" "}

            {restaurantName.toUpperCase()}

          </p>


          <h2>

            From India

            <br />

            <i>
              to your table.
            </i>

          </h2>


          <p className="t6-intro-text">

            We celebrate the simple beauty of
            Flavours of India cooking with fresh ingredients,
            local ingredients and recipes inspired by
            generations of Indian tradition.

          </p>

        </div>

      </section>



      {/* ==================================================
          STORY
      ================================================== */}

      <section className="t6-story">

        <div className="t6-story-image">

          <img
            src="https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=1200&q=85"
            alt="Flavours of India food"
          />

        </div>


        <div className="t6-story-content">

          <p className="t6-label">
            OUR STORY
          </p>


          <h2>

            A table by

            <br />

            <i>
              India.
            </i>

          </h2>


          <p>

            Indian Restaurant brings together the flavours
            of the Indian islands with fresh
            ingredients from our kitchens.

          </p>


          <p>

            Every dish is prepared with care, simplicity
            and respect for Indian culinary tradition.

          </p>


          <button
            type="button"
            className="t6-link-button"
            onClick={scrollToMenu}
          >

            Discover our food

            <span>
              →
            </span>

          </button>

        </div>

      </section>



      {/* ==================================================
          FAVOURITES
      ================================================== */}

      {favouriteItems.length > 0 && (

        <section className="t6-favourites">

          <div className="t6-container">

            <div className="t6-section-header">

              <p className="t6-label">
                FROM OUR KITCHEN
              </p>


              <h2>

                Our

                <br />

                <i>
                  Favourites
                </i>

              </h2>


              <p>
                A selection of dishes our guests love.
              </p>

            </div>

          </div>


          <div className="t6-favourite-window">

            <div className="t6-favourite-track">


              {/* ==================================================
                  FIRST 12
              ================================================== */}

              {favouriteItems.map(
                (item) => (

                  <article
                    className="t6-favourite-card"
                    key={`first-${item.id}`}
                  >


                    <div className="t6-favourite-image">

                      {item.image ? (

                        <img
                          src={item.image}
                          alt={item.name}
                        />

                      ) : (

                        <div className="t6-no-image">
                          No image
                        </div>

                      )}


                      <span>
                        {item.categoryName}
                      </span>

                    </div>


                    <div className="t6-favourite-body">

                      <div>

                        <h3>
                          {item.name}
                        </h3>


                        {item.description && (

                          <p>
                            {item.description}
                          </p>

                        )}

                      </div>


                      <div className="t6-favourite-bottom">

                        <strong>

                          $
                          {Number(
                            item.price
                          ).toFixed(2)}

                        </strong>


                        <button
                          type="button"
                          onClick={() =>
                            handleAddToCart(
                              item
                            )
                          }
                          aria-label={
                            `Add ${item.name}`
                          }
                        >
                          +
                        </button>

                      </div>

                    </div>

                  </article>

                )
              )}



              {/* ==================================================
                  SECOND 12
                  Duplicate for seamless animation
              ================================================== */}

              {favouriteItems.map(
                (item) => (

                  <article
                    className="t6-favourite-card"
                    key={`second-${item.id}`}
                  >


                    <div className="t6-favourite-image">

                      {item.image ? (

                        <img
                          src={item.image}
                          alt={item.name}
                        />

                      ) : (

                        <div className="t6-no-image">
                          No image
                        </div>

                      )}


                      <span>
                        {item.categoryName}
                      </span>

                    </div>


                    <div className="t6-favourite-body">

                      <div>

                        <h3>
                          {item.name}
                        </h3>


                        {item.description && (

                          <p>
                            {item.description}
                          </p>

                        )}

                      </div>


                      <div className="t6-favourite-bottom">

                        <strong>

                          $
                          {Number(
                            item.price
                          ).toFixed(2)}

                        </strong>


                        <button
                          type="button"
                          onClick={() =>
                            handleAddToCart(
                              item
                            )
                          }
                          aria-label={
                            `Add ${item.name}`
                          }
                        >
                          +
                        </button>

                      </div>

                    </div>

                  </article>

                )
              )}

            </div>

          </div>

        </section>

      )}



      {/* ==================================================
          MENU
      ================================================== */}

      <section
        id="menu"
        className="t6-menu-section"
      >

        <div className="t6-container">


          {/* ==================================================
              MENU HEADER
          ================================================== */}

          <div className="t6-section-header t6-menu-header">

            <p className="t6-label">
              OUR MENU
            </p>


            <h2>

              Taste the

              <br />

              <i>
                India
              </i>

            </h2>


            <p>
              Fresh ingredients. Honest flavours.
              Flavours of India soul.
            </p>

          </div>



          {/* ==================================================
              MENU
          ================================================== */}

          {categories.length === 0 ? (

            <div className="t6-empty-menu">

              <div className="t6-empty-menu-icon">
                ✦
              </div>


              <h3>
                Menu coming soon
              </h3>


              <p>
                Our kitchen is preparing something special.
              </p>

            </div>

          ) : (

            <>


              {/* ==================================================
                  CATEGORY NAVIGATION
              ================================================== */}

              <div className="t6-category-nav">

                {categories.map(
                  (category) => (

                    <button
                      key={category.id}
                      type="button"
                      className="t6-category-nav-button"
                      onClick={() =>
                        scrollToCategory(
                          category.id
                        )
                      }
                    >

                      <span className="t6-category-nav-number">

                        {String(
                          category.display_order || 0
                        ).padStart(
                          2,
                          "0"
                        )}

                      </span>


                      <span className="t6-category-nav-name">

                        {category.name}

                      </span>

                    </button>

                  )
                )}

              </div>



              {/* ==================================================
                  BACKEND MENU CATEGORIES
              ================================================== */}

              <div className="t6-categories">

                {categories.map(
                  (category) => (

                    <section
                      id={
                        `t6-category-${category.id}`
                      }
                      className="t6-category"
                      key={category.id}
                    >


                      {/* ==========================================
                          CATEGORY HEADER
                      ========================================== */}

                      <div className="t6-category-header">

                        <div className="t6-category-title">

                          <span className="t6-category-number">

                            {String(
                              category.display_order || 0
                            ).padStart(
                              2,
                              "0"
                            )}

                          </span>


                          <div>

                            <h3>
                              {category.name}
                            </h3>


                            {category.description && (

                              <p>
                                {category.description}
                              </p>

                            )}

                          </div>

                        </div>


                        {category.image && (

                          <img
                            className="t6-category-image"
                            src={category.image}
                            alt={category.name}
                          />

                        )}

                      </div>



                      {/* ==========================================
                          MENU ITEMS
                      ========================================== */}

                      <div className="t6-items">

                        {category.items.length === 0 ? (

                          <p className="t6-no-items">
                            No items available.
                          </p>

                        ) : (

                          category.items.map(
                            (item) => (

                              <article
                                className="t6-item"
                                key={item.id}
                              >


                                <div className="t6-item-image">

                                  {item.image ? (

                                    <img
                                      src={item.image}
                                      alt={item.name}
                                    />

                                  ) : (

                                    <div className="t6-no-image">
                                      No image
                                    </div>

                                  )}

                                </div>



                                <div className="t6-item-content">


                                  <div className="t6-item-top">

                                    <h4>
                                      {item.name}
                                    </h4>


                                    <strong>

                                      $

                                      {Number(
                                        item.price
                                      ).toFixed(2)}

                                    </strong>

                                  </div>



                                  {item.description && (

                                    <p>
                                      {item.description}
                                    </p>

                                  )}



                                  <button
                                    type="button"
                                    className="t6-add-button"
                                    onClick={() =>
                                      handleAddToCart(
                                        item
                                      )
                                    }
                                  >
                                    Add to order
                                  </button>


                                </div>

                              </article>

                            )
                          )

                        )}

                      </div>

                    </section>

                  )
                )}

              </div>

            </>

          )}

        </div>

      </section>



      {/* ==================================================
          SEA FEATURE
      ================================================== */}

      <section className="t6-sea-feature">

        <div className="t6-sea-image">

          <img
            src="https://images.unsplash.com/photo-1534604973900-c43ab4c2e0ab?auto=format&fit=crop&w=1400&q=85"
            alt="Fresh Indian cuisine"
          />

        </div>


        <div className="t6-sea-content">

          <p className="t6-label">
            FRESH FROM THE SEA
          </p>


          <h2>

            The sea

            <br />

            <i>
              inspires our kitchen.
            </i>

          </h2>


          <p>

            Our ingredients are selected for freshness and
            prepared simply to let the natural flavours
            shine.

          </p>


          <button
            type="button"
            className="t6-link-button"
            onClick={scrollToMenu}
          >

            Explore Indian cuisine

            <span>
              →
            </span>

          </button>

        </div>

      </section>



      {/* ==================================================
          EXPERIENCE
      ================================================== */}

      <section className="t6-experience">

        <div className="t6-container">

          <div className="t6-section-header">

            <p className="t6-label">
              THE INDIAN DINING EXPERIENCE
            </p>


            <h2>

              Good food.

              <br />

              <i>
                Good company.
              </i>

            </h2>

          </div>



          <div className="t6-experience-grid">


            {/* CARD 01 */}

            <div className="t6-experience-card">

              <span>
                01
              </span>


              <h3>
                Fresh Ingredients
              </h3>


              <p>
                Carefully selected ingredients
                from our kitchens.
              </p>

            </div>



            {/* CARD 02 */}

            <div className="t6-experience-card">

              <span>
                02
              </span>


              <h3>
                Indian Tradition
              </h3>


              <p>
                Authentic recipes with a modern
                Indian touch.
              </p>

            </div>



            {/* CARD 03 */}

            <div className="t6-experience-card">

              <span>
                03
              </span>


              <h3>
                Shared Moments
              </h3>


              <p>
                Food tastes better when it is
                shared around the table.
              </p>

            </div>


          </div>

        </div>

      </section>



      {/* ==================================================
          GALLERY
      ================================================== */}

      <section className="t6-gallery">

        <div className="t6-container">


          <div className="t6-section-header">

            <p className="t6-label">
              MOMENTS TO SAVOUR
            </p>


            <h2>

              A taste of

              <br />

              <i>
                the Flavours of India
              </i>

            </h2>

          </div>



          <div className="t6-gallery-grid">


            {/* LARGE IMAGE */}

            <div className="t6-gallery-large">

              <img
                src="https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=1200&q=85"
                alt="Indian food"
              />

            </div>



            {/* IMAGE 2 */}

            <div>

              <img
                src="https://images.unsplash.com/photo-1534604973900-c43ab4c2e0ab?auto=format&fit=crop&w=900&q=85"
                alt="Indian food"
              />

            </div>



            {/* IMAGE 3 */}

            <div>

              <img
                src="https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&w=900&q=85"
                alt="Restaurant"
              />

            </div>



            {/* IMAGE 4 */}

            <div>

              <img
                src="https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?auto=format&fit=crop&w=900&q=85"
                alt="Dining"
              />

            </div>


          </div>

        </div>

      </section>



      {/* ==================================================
          FLOATING CART
      ================================================== */}

      {cartCount > 0 && (

        <button
          type="button"
          className="t6-floating-cart"
          onClick={openCart}
        >

          <span className="t6-cart-count">
            {cartCount}
          </span>


          <span className="t6-cart-text">
            View Cart
          </span>


          <strong>

            $
            {Number(
              cartTotal || 0
            ).toFixed(2)}

          </strong>

        </button>

      )}


    </main>

  );

}


export default HomeScreen;